import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ATELIER, FONTS, ICON } from '../tokens';
import { ModalityBadge } from './ModalityBadge';
import { RevealEffect } from '../stage/RevealEffect';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Download, Maximize2, ExternalLink, Sparkles, Music, Copy, Check, FileSpreadsheet } from 'lucide-react';
import { useWorkspace } from '../WorkspaceContext';
import boneyardRegistry from '../../boneyard.registry.json';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneLight } from 'react-syntax-highlighter/dist/esm/styles/prism';

/**
 * TurnCard - single turn in the atelier thread (user OR assistant).
 * Rendered with a left-rail badge + body content.
 *
 * The wrapper exposes:
 *   data-turn-id={turn.id}   - for MemoryRibbon attachment
 *   [data-badge]             - anchor element on the badge node
 *
 * Spine line: a faint vertical line connects badge → next badge, giving the
 * thread its "rhythm" feel.
 */
export function TurnCard({ turn, isLast, onPickReference }) {
  const isUser = turn.role === 'user';

  if (isUser) {
    return (
      <motion.div
        data-turn-id={turn.id}
        layout="position"
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.32, ease: [0.22, 1, 0.36, 1] }}
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          position: 'relative',
          paddingBottom: isLast ? 0 : 28,
        }}
      >
        <div style={{ maxWidth: '75%', display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
          <UserBody turn={turn} />
          <UserMeta turn={turn} />
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      data-turn-id={turn.id}
      layout="position"
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.32, ease: [0.22, 1, 0.36, 1] }}
      style={{
        display: 'grid',
        gridTemplateColumns: '32px 1fr',
        columnGap: 14,
        position: 'relative',
        paddingBottom: isLast ? 0 : 32,
      }}
    >
      {/* Left rail: badge (NO spine line for new ChatGPT style) */}
      <div style={{
        position: 'relative',
        display: 'flex', flexDirection: 'column', alignItems: 'center',
      }}>
        <div data-badge style={{ width: 28, height: 28, borderRadius: '50%', overflow: 'hidden', flexShrink: 0, border: `1px solid ${ATELIER.borderSoft}`, boxShadow: '0 2px 4px rgba(0,0,0,0.04)' }}>
          <img src="/DAGGPT-01.png" alt="DAGGPT" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        </div>
      </div>

      {/* Body */}
      <div style={{ minWidth: 0, paddingTop: 2 }}>
        <AssistantMeta turn={turn} />
        <AssistantBody turn={turn} />
      </div>
    </motion.div>
  );
}

function AssistantMeta({ turn }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 8,
      marginBottom: 8,
      fontSize: 13, color: ATELIER.inkSoft,
      fontFamily: FONTS.sans,
    }}>
      <span style={{ fontWeight: 600, color: ATELIER.ink, letterSpacing: '-0.005em' }}>
        DAGGPT
      </span>
      <span style={{
        fontFamily: FONTS.mono, fontSize: 9.5, letterSpacing: '0.16em',
        color: ATELIER.inkMuted, fontWeight: 600,
        textTransform: 'uppercase',
      }}>
        {turn.type}
      </span>
    </div>
  );
}

/* ── User Meta (Intent Source / Time) ────────── */
function UserMeta({ turn }) {
  const [copied, setCopied] = useState(false);
  const intentSource = turn.meta?.intentSource;
  const showIntent = intentSource && intentSource !== 'default' && turn.type !== 'chat';

  const handleCopy = async () => {
    if (!turn?.text) return;
    try {
      await navigator.clipboard.writeText(turn.text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy prompt:', err);
    }
  };

  return (
    <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 8 }}>
      {showIntent && (
        <span style={{
          display: 'inline-flex', alignItems: 'center', gap: 4,
          padding: '2px 7px',
          background: ATELIER.bgHover,
          border: `1px solid ${ATELIER.borderSoft}`,
          borderRadius: 999,
          fontFamily: FONTS.mono, fontSize: 9.5, letterSpacing: '0.14em',
          color: ATELIER.inkSoft, fontWeight: 700,
          textTransform: 'uppercase',
        }}>
          Auto-routed: {turn.type}
        </span>
      )}
      <button
        type="button"
        onClick={handleCopy}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 4.5,
          padding: '3px 8px',
          background: 'transparent',
          border: 'none',
          borderRadius: 6,
          fontSize: 11,
          fontWeight: 500,
          color: ATELIER.inkSoft,
          cursor: 'pointer',
          transition: 'all 0.15s ease-in-out',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.color = ATELIER.ink;
          e.currentTarget.style.background = 'rgba(0,0,0,0.03)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.color = ATELIER.inkSoft;
          e.currentTarget.style.background = 'transparent';
        }}
      >
        {copied ? (
          <>
            <Check size={11} strokeWidth={2} style={{ color: ATELIER.accent }} />
            <span style={{ color: ATELIER.accent }}>Copied</span>
          </>
        ) : (
          <>
            <Copy size={11} strokeWidth={2} />
            <span>Copy prompt</span>
          </>
        )}
      </button>
    </div>
  );
}

/* ── USER BODY ──────────────────────────────────────────────── */
function UserBody({ turn }) {
  return (
    <div style={{
      fontFamily: FONTS.sans,
      fontSize: 15,
      lineHeight: 1.6,
      color: ATELIER.ink,
      letterSpacing: '-0.005em',
      whiteSpace: 'pre-wrap',
      wordBreak: 'break-word',
      background: ATELIER.bgHover,
      border: `1px solid ${ATELIER.border}`,
      padding: '12px 18px',
      borderRadius: '20px 20px 4px 20px',
      boxShadow: '0 1px 2px rgba(0,0,0,0.02)',
    }}>
      {turn.text}
      {turn.meta?.attachments && turn.meta.attachments.length > 0 && (
        <div style={{ 
          display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap', justifyContent: 'flex-end',
          borderTop: `1px solid ${ATELIER.border}`, paddingTop: 10
        }}>
          {turn.meta.attachments.map(a => (
            <AttachmentCard key={a.id} attachment={a} />
          ))}
        </div>
      )}
    </div>
  );
}

function AttachmentCard({ attachment: a }) {
  const isImage = (a.type?.startsWith('image/') || a.url?.match(/\.(jpg|jpeg|png|webp|gif)$/i));
  
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 8,
      padding: '6px 10px',
      background: '#fff',
      border: `1px solid ${ATELIER.border}`,
      borderRadius: 10,
      fontSize: 12,
      color: ATELIER.inkMid,
      boxShadow: '0 2px 4px rgba(0,0,0,0.02)'
    }}>
      {isImage ? (
        <div style={{ width: 24, height: 24, borderRadius: 4, overflow: 'hidden', border: `1px solid ${ATELIER.borderSoft}` }}>
          <img src={a.url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        </div>
      ) : (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={ATELIER.inkSoft} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
          <polyline points="14 2 14 8 20 8" />
        </svg>
      )}
      <span style={{ maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: 500 }}>
        {a.name}
      </span>
      <a href={a.url} target="_blank" rel="noreferrer" style={{ color: ATELIER.accent, display: 'flex', alignItems: 'center' }}>
        <ExternalLink size={12} />
      </a>
    </div>
  );
}

/* ── ASSISTANT BODY ─────────────────────────────────────────── */
function AssistantBody({ turn }) {
  const { status } = turn;

  if (status === 'error' || status === 'failed') return <ErrorBody turn={turn} error={turn.error} />;
  
  if (turn.status === 'pending') {
    if (turn.type === 'chat' || turn.type === 'slides') {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {turn.text && <TextBody turn={turn} />}
          <SmartChatLoader turn={turn} />
        </div>
      );
    }
    return <PendingBody turn={turn} />;
  }

  if (turn.type === 'chat' || turn.type === 'slides')   return <TextBody turn={turn} />;
  if (turn.type === 'image')  return <ImageBody turn={turn} />;
  if (turn.type === 'video')  return <VideoBody turn={turn} />;
  if (turn.type === 'music')  return <MusicBody turn={turn} />;
  return null;
}

/* ── Pending ────────────────────────────────────────────────── */
function PendingBody({ turn }) {
  const type = turn.type;
  if (type === 'chat') {
    return <SmartChatLoader turn={turn} />;
  }
  if (type === 'music') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {[1, 2].map(num => (
          <div key={num} style={{
            maxWidth: 560,
            padding: 14,
            borderRadius: 14,
            border: `1px solid ${ATELIER.border}`,
            background: 'linear-gradient(135deg, #FCFAFE 0%, #F4EEFE 100%)',
            display: 'flex', alignItems: 'center', gap: 14,
          }}>
            <div style={{ width: 44, height: 44, borderRadius: 8, background: ATELIER.bgHover, flexShrink: 0 }} />
            <div style={{ width: 40, height: 40, borderRadius: '50%', background: ATELIER.bgHover, flexShrink: 0 }} />
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 4 }}>
              <div style={{ width: 120, height: 14, background: ATELIER.bgHover, borderRadius: 4 }} />
              <div style={{ display: 'flex', alignItems: 'center', gap: 2.5, height: 26 }}>
                {Array.from({ length: 44 }).map((_, i) => (
                  <motion.div
                    key={i}
                    animate={{ height: [6, 20, 6], opacity: [0.4, 1, 0.4] }}
                    transition={{ duration: 0.9, repeat: Infinity, delay: i * 0.025 + num * 0.2, ease: 'easeInOut' }}
                    style={{ width: 2.5, borderRadius: 2, background: ATELIER.accent }}
                  />
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }
  // image / video shimmer
  const ratio = type === 'video' ? '16 / 9' : '1 / 1';
  return (
    <div style={{
      maxWidth: type === 'video' ? 600 : 540,
      borderRadius: 14,
      overflow: 'hidden',
      border: `1px solid ${ATELIER.border}`,
      background: 'linear-gradient(135deg, #FCFAFE 0%, #F4EEFE 100%)',
      position: 'relative',
    }}>
      <div style={{
        width: '100%', aspectRatio: ratio,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        position: 'relative',
        overflow: 'hidden',
      }}>
        <motion.div
          aria-hidden
          animate={{ backgroundPosition: ['0% 0%', '-200% 0%'] }}
          transition={{ duration: 2.4, repeat: Infinity, ease: 'linear' }}
          style={{
            position: 'absolute', inset: 0,
            background: 'linear-gradient(115deg, transparent 30%, rgba(167,139,250,0.30) 50%, transparent 70%)',
            backgroundSize: '200% 100%',
          }}
        />
        <span style={{
          fontFamily: FONTS.mono, fontSize: 11, letterSpacing: '0.22em',
          color: ATELIER.accent, fontWeight: 700, textTransform: 'uppercase',
          zIndex: 1,
        }}>
          {type === 'video' ? 'Generating video' : 'Rendering image'}
        </span>
      </div>
    </div>
  );
}

/* ── Text ───────────────────────────────────────────────────── */
function CitationPill({ id, citations, accentColor }) {
  const citation = citations?.find(c => c.index === parseInt(id) || c.id === id || String(c.id) === String(id));
  const url = citation?.url || citation?.href || '';
  
  // Extract a clean site name
  const getDisplayLabel = () => {
    if (citation?.title && !citation.title.includes('http')) {
      // If it's a long title, truncate it nicely
      return citation.title.length > 20 ? citation.title.substring(0, 18) + '...' : citation.title;
    }
    if (!url) return `Source ${id}`;
    try {
      const domain = new URL(url).hostname.replace('www.', '');
      return domain.split('.')[0].charAt(0).toUpperCase() + domain.split('.')[0].slice(1);
    } catch {
      return `Source ${id}`;
    }
  };

  const displayLabel = getDisplayLabel();

  return (
    <motion.a
      href={url || '#'}
      target="_blank"
      rel="noopener noreferrer"
      whileHover={{ scale: 1.05, y: -1, background: accentColor, color: '#fff' }}
      whileTap={{ scale: 0.95 }}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        background: `${accentColor}12`,
        color: accentColor,
        padding: '2px 9px',
        borderRadius: 8,
        fontSize: 11,
        fontWeight: 600,
        textDecoration: 'none',
        margin: '0 3px',
        border: `1px solid ${accentColor}25`,
        verticalAlign: 'middle',
        cursor: url ? 'pointer' : 'default',
        boxShadow: `0 1px 2px ${accentColor}08`,
        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
      }}
    >
      <span style={{ opacity: 0.9 }}>{displayLabel}</span>
      <ExternalLink size={10} style={{ opacity: 0.6 }} />
    </motion.a>
  );
}

/* ── Smart Image Generator (Inline & Automatic) ────────────────────────── */
function SmartImageGenerator({ prompt, accentColor }) {
  const { generateUnified } = useWorkspace();
  const [status, setStatus] = useState('generating'); // Start immediately
  const [imageUrl, setImageUrl] = useState(null);
  const [error, setError] = useState(null);
  const initialized = useRef(false);

  const handleGenerate = async () => {
    if (initialized.current) return;
    initialized.current = true;
    
    try {
      const { taskId } = await generateUnified({ 
        mode: 'image', 
        messages: [{ role: 'user', content: prompt }],
        options: { aspect: '1:1', style: 'Photorealistic' }
      });
      
      let attempts = 0;
      const poll = setInterval(async () => {
        attempts++;
        if (attempts > 60) {
          clearInterval(poll);
          setStatus('error');
          setError('Generation timed out.');
          return;
        }

        const res = await fetch(`https://api.daggpt.network/api/unified/status/${taskId}`);
        if (res.ok) {
          const data = await res.json();
          if (data.status === 'done' && data.imageUrl) {
            clearInterval(poll);
            setImageUrl(data.imageUrl);
            setStatus('done');
          } else if (data.status === 'error') {
            clearInterval(poll);
            setStatus('error');
            setError(data.error || 'Generation failed.');
          }
        }
      }, 2000);

    } catch (err) {
      console.error('[SmartImage] Failed:', err);
      setStatus('error');
      setError(err.message);
    }
  };

  useEffect(() => {
    handleGenerate();
  }, []);

  return (
    <motion.div 
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      style={{
        width: '100%',
        maxWidth: 380,
        borderRadius: 20,
        overflow: 'hidden',
        border: `1px solid ${ATELIER.border}`,
        background: ATELIER.bgHover,
        position: 'relative',
        boxShadow: ATELIER.shadowL,
        margin: '16px auto'
      }}
    >
      {status === 'generating' && (
        <BoneyardSkeleton name="image" />
      )}

      {status === 'done' && imageUrl && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
          <img 
            src={imageUrl} 
            alt={prompt} 
            style={{ width: '100%', display: 'block' }} 
          />
          <div style={{ padding: '16px 20px', background: '#fff', borderTop: `1px solid ${ATELIER.borderSoft}` }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Sparkles size={12} color={accentColor} />
              <p style={{ margin: 0, fontSize: 12, color: ATELIER.inkMid, fontWeight: 500 }}>AI Generated Vision</p>
            </div>
          </div>
        </motion.div>
      )}

      {status === 'error' && (
        <div style={{ padding: 32, textAlign: 'center', color: '#ef4444' }}>
          <p style={{ margin: 0, fontSize: 13, fontWeight: 500 }}>{error || 'Failed to generate visual.'}</p>
          <button onClick={() => { setStatus('generating'); initialized.current = false; handleGenerate(); }} style={{ marginTop: 12, fontSize: 12, background: 'none', border: 'none', color: accentColor, cursor: 'pointer', fontWeight: 600 }}>Try again</button>
        </div>
      )}
    </motion.div>
  );
}

/* ── Smart Video Generator ────────────────────────────────────────── */
function SmartVideoGenerator({ prompt, accentColor, aspect = '16:9' }) {
  const { generateUnified } = useWorkspace();
  const [status, setStatus] = useState('generating');
  const [videoUrl, setVideoUrl] = useState(null);
  const [error, setError] = useState(null);
  const initialized = useRef(false);

  const handleGenerate = async () => {
    if (initialized.current) return;
    initialized.current = true;
    try {
      const { taskId } = await generateUnified({ 
        mode: 'video', 
        messages: [{ role: 'user', content: prompt }],
        options: { aspect_ratio: aspect }
      });
      
      let attempts = 0;
      const poll = setInterval(async () => {
        attempts++;
        if (attempts > 100) { // Video takes longer
          clearInterval(poll);
          setStatus('error');
          setError('Generation timed out.');
          return;
        }

        const res = await fetch(`https://api.daggpt.network/api/unified/status/${taskId}`);
        if (res.ok) {
          const data = await res.json();
          if (data.status === 'done' && data.imageUrl) {
            clearInterval(poll);
            setVideoUrl(data.imageUrl); // imageUrl is used for both image/video in status
            setStatus('done');
          } else if (data.status === 'failed' || data.status === 'error') {
            clearInterval(poll);
            setStatus('error');
            setError(data.error || 'Generation failed.');
          }
        }
      }, 3000);
    } catch (err) {
      setStatus('error');
      setError(err.message);
    }
  };

  useEffect(() => { handleGenerate(); }, []);

  return (
    <motion.div 
      style={{ width: '100%', maxWidth: 380, borderRadius: 20, overflow: 'hidden', border: `1px solid ${ATELIER.border}`, background: '#000', margin: '16px auto', position: 'relative' }}
    >
      {status === 'generating' && <BoneyardSkeleton name="video" aspect={aspect} />}
      {status === 'done' && videoUrl && (
        <video src={videoUrl} controls playsInline style={{ width: '100%', display: 'block' }} />
      )}
      {status === 'error' && <div style={{ padding: 20, textAlign: 'center', color: '#ef4444' }}>{error}</div>}
    </motion.div>
  );
}

/* ── Smart Music Generator ────────────────────────────────────────── */
function SmartMusicGenerator({ prompt, accentColor }) {
  const { generateUnified } = useWorkspace();
  const [status, setStatus] = useState('generating');
  const [audioUrl, setAudioUrl] = useState(null);
  const [error, setError] = useState(null);
  const initialized = useRef(false);

  const handleGenerate = async () => {
    if (initialized.current) return;
    initialized.current = true;
    try {
      const { taskId } = await generateUnified({ 
        mode: 'music', 
        messages: [{ role: 'user', content: prompt }],
        options: { vocal: true }
      });
      
      let attempts = 0;
      const poll = setInterval(async () => {
        attempts++;
        if (attempts > 120) {
          clearInterval(poll);
          setStatus('error');
          setError('Generation timed out.');
          return;
        }

        const res = await fetch(`https://api.daggpt.network/api/unified/status/${taskId}`);
        if (res.ok) {
          const data = await res.json();
          if (data.status === 'done' && data.imageUrl) {
            clearInterval(poll);
            setAudioUrl(data.imageUrl);
            setStatus('done');
          } else if (data.status === 'failed' || data.status === 'error') {
            clearInterval(poll);
            setStatus('error');
            setError(data.error || 'Generation failed.');
          }
        }
      }, 3000);
    } catch (err) {
      setStatus('error');
      setError(err.message);
    }
  };

  useEffect(() => { handleGenerate(); }, []);

  return (
    <motion.div style={{ width: '100%', maxWidth: 380, margin: '16px auto' }}>
      {status === 'generating' && <BoneyardSkeleton name="music" />}
      {status === 'done' && audioUrl && (
        <div style={{ padding: 16, background: ATELIER.bgHover, borderRadius: 32, display: 'flex', alignItems: 'center', gap: 12, border: `1px solid ${ATELIER.border}` }}>
          <div style={{ width: 40, height: 40, borderRadius: '50%', background: ATELIER.accent, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff' }}>
            <Music size={20} />
          </div>
          <audio src={audioUrl} controls style={{ height: 32, flex: 1 }} />
        </div>
      )}
      {status === 'error' && <div style={{ color: '#ef4444', textAlign: 'center' }}>{error}</div>}
    </motion.div>
  );
}

/* ── Web Image Gallery (Instant Web-Vision) ────────────────── */
function WebImage({ url }) {
  return (
    <motion.div 
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      whileHover={{ y: -4 }}
      style={{
        width: '100%',
        maxWidth: 380,
        borderRadius: 24,
        overflow: 'hidden',
        border: `1px solid ${ATELIER.border}`,
        background: '#fff',
        boxShadow: ATELIER.shadowM,
        margin: '16px auto',
        cursor: 'pointer'
      }}
      onClick={() => window.open(url, '_blank')}
    >
      <div style={{ position: 'relative' }}>
        <img 
          src={url} 
          alt="Web Source" 
          style={{ width: '100%', height: 'auto', display: 'block', maxHeight: 400, objectFit: 'cover' }} 
        />
        <div style={{ 
          position: 'absolute', 
          top: 16, 
          right: 16, 
          padding: '6px 12px', 
          background: 'rgba(255,255,255,0.9)', 
          backdropFilter: 'blur(10px)',
          borderRadius: 100,
          border: `1px solid ${ATELIER.border}`,
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
        }}>
          <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#10b981' }} />
          <span style={{ fontSize: 10, fontWeight: 700, color: ATELIER.ink, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Web Source</span>
        </div>
      </div>
    </motion.div>
  );
}

/* ── Text ───────────────────────────────────────────────────── */
/* ── Spreadsheet Card Widget ───────────────────────────────── */
function SpreadsheetWidgetCard({ title, sheetCount, onOpen }) {
  return (
    <div 
      onClick={onOpen}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '14px 18px',
        borderRadius: 16,
        background: 'linear-gradient(135deg, #ECFDF5 0%, #F0FDF4 100%)',
        border: '1px solid #10B981',
        cursor: 'pointer',
        boxShadow: '0 4px 12px rgba(16,185,129,0.06)',
        marginTop: 14,
        marginBottom: 14,
        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-2px)';
        e.currentTarget.style.boxShadow = '0 6px 16px rgba(16,185,129,0.1)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = '0 4px 12px rgba(16,185,129,0.06)';
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <div style={{
          width: 40, height: 40, borderRadius: 10,
          background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: '#FFFFFF',
          boxShadow: '0 4px 10px rgba(16,185,129,0.15)'
        }}>
          <FileSpreadsheet size={20} />
        </div>
        <div>
          <h4 style={{ margin: 0, fontSize: 13, fontWeight: 700, color: '#065F46', fontFamily: FONTS.sans }}>
            {title}
          </h4>
          <span style={{ fontSize: 11, color: '#047857', fontFamily: FONTS.mono, fontWeight: 500, display: 'flex', alignItems: 'center', gap: 4 }}>
            <span>Interactive Financial Model</span>
            <span>•</span>
            <span>{sheetCount} Sheet{sheetCount > 1 ? 's' : ''}</span>
          </span>
        </div>
      </div>

      <button style={{
        padding: '6px 14px',
        borderRadius: 8,
        background: '#10B981',
        border: 'none',
        color: '#FFFFFF',
        fontSize: 12,
        fontWeight: 600,
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        boxShadow: '0 2px 6px rgba(16,185,129,0.2)'
      }}
      onMouseEnter={(e) => { e.currentTarget.style.background = '#059669'; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = '#10B981'; }}>
        View Sheet
      </button>
    </div>
  );
}

/* ── Presentation Card Widget ───────────────────────────────── */
function PresentationWidgetCard({ title, slideCount, onOpen }) {
  return (
    <div 
      onClick={onOpen}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '14px 18px',
        borderRadius: 16,
        background: 'linear-gradient(135deg, #ECFDF5 0%, #F0FDF4 100%)',
        border: '1px solid #10B981',
        cursor: 'pointer',
        boxShadow: '0 4px 12px rgba(16,185,129,0.06)',
        marginTop: 14,
        marginBottom: 14,
        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-2px)';
        e.currentTarget.style.boxShadow = '0 6px 16px rgba(16,185,129,0.1)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = '0 4px 12px rgba(16,185,129,0.06)';
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <div style={{
          width: 40, height: 40, borderRadius: 10,
          background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: '#FFFFFF',
          boxShadow: '0 4px 10px rgba(16,185,129,0.15)'
        }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
            <line x1="8" y1="21" x2="16" y2="21" />
            <line x1="12" y1="17" x2="12" y2="21" />
          </svg>
        </div>
        <div>
          <h4 style={{ margin: 0, fontSize: 13, fontWeight: 700, color: '#065F46', fontFamily: FONTS.sans }}>
            {title}
          </h4>
          <span style={{ fontSize: 11, color: '#047857', fontFamily: FONTS.mono, fontWeight: 500, display: 'flex', alignItems: 'center', gap: 4 }}>
            <span>Landscape Presentation Studio</span>
            <span>•</span>
            <span>{slideCount} Slide{slideCount !== 1 ? 's' : ''}</span>
          </span>
        </div>
      </div>

      <button style={{
        padding: '6px 14px',
        borderRadius: 8,
        background: '#10B981',
        border: 'none',
        color: '#FFFFFF',
        fontSize: 12,
        fontWeight: 600,
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        boxShadow: '0 2px 6px rgba(16,185,129,0.2)'
      }}
      onMouseEnter={(e) => { e.currentTarget.style.background = '#059669'; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = '#10B981'; }}>
        Open Slides Studio
      </button>
    </div>
  );
}

/* ── Text ───────────────────────────────────────────────────── */
function TextBody({ turn }) {
  const { activePreview, setActivePreview } = useWorkspace();
  const citations = turn.meta?.citations || [];
  const [hasAutoOpened, setHasAutoOpened] = useState(false);
  const [displayedText, setDisplayedText] = useState(() => {
    // If turn is already done, show full text immediately to avoid re-typing on refresh
    return turn.status === 'done' ? (turn.text || '') : '';
  });
  const targetTextRef = useRef(turn.text || '');
  const isStreaming = turn.status === 'pending';
  const cursorRef = useRef(turn.status === 'done' ? (turn.text || '').length : 0);

  // Sync target text ref
  useEffect(() => {
    targetTextRef.current = turn.text || '';
  }, [turn.text]);

  useEffect(() => {
    const interval = setInterval(() => {
      const currentTarget = targetTextRef.current;
      if (cursorRef.current < currentTarget.length) {
        const remaining = currentTarget.length - cursorRef.current;
        let step = 1;
        if (remaining > 200) step = 15;
        else if (remaining > 100) step = 8;
        else if (remaining > 50) step = 4;
        else if (remaining > 20) step = 2;
        
        cursorRef.current = Math.min(currentTarget.length, cursorRef.current + step);
        setDisplayedText(currentTarget.substring(0, cursorRef.current));
      } else if (!isStreaming) {
        clearInterval(interval);
      }
    }, 20);

    return () => clearInterval(interval);
  }, [isStreaming]); // Keep isStreaming so we restart if status changes

  // Parse spreadsheet data if present
  const hasSpreadsheet = turn.text?.includes('[SPREADSHEET_DATA]');
  let spreadsheetData = null;
  if (hasSpreadsheet) {
    try {
      const match = turn.text.match(/\[SPREADSHEET_DATA\]([\s\S]*?)\[\/SPREADSHEET_DATA\]/);
      if (match) {
        let rawJson = match[1].trim();
        // Remove markdown backticks if LLM wrapped it
        if (rawJson.startsWith('```')) {
          rawJson = rawJson.replace(/^```[a-zA-Z]*\s*/, '').replace(/\s*```$/, '');
        }
        rawJson = rawJson.trim();
        // Isolate content strictly between the first '{' and last '}' to strip LLM prefixes/suffixes
        const firstBrace = rawJson.indexOf('{');
        const lastBrace = rawJson.lastIndexOf('}');
        if (firstBrace !== -1 && lastBrace !== -1 && lastBrace > firstBrace) {
          rawJson = rawJson.substring(firstBrace, lastBrace + 1);
        }
        spreadsheetData = JSON.parse(rawJson);
      }
    } catch (e) {
      console.warn('[TurnCard] Failed to parse spreadsheet data:', e);
    }
  }

  // Parse presentation data if present
  const hasPresentation = turn.text?.includes('[PRESENTATION_DATA]');
  let presentationData = null;
  if (hasPresentation) {
    try {
      const match = turn.text.match(/\[PRESENTATION_DATA\]([\s\S]*?)\[\/PRESENTATION_DATA\]/);
      if (match) {
        let rawJson = match[1].trim();
        // Remove markdown backticks if LLM wrapped it
        if (rawJson.startsWith('```')) {
          rawJson = rawJson.replace(/^```[a-zA-Z]*\s*/, '').replace(/\s*```$/, '');
        }
        rawJson = rawJson.trim();
        // Isolate content strictly between the first '{' and last '}' to strip LLM prefixes/suffixes
        const firstBrace = rawJson.indexOf('{');
        const lastBrace = rawJson.lastIndexOf('}');
        if (firstBrace !== -1 && lastBrace !== -1 && lastBrace > firstBrace) {
          rawJson = rawJson.substring(firstBrace, lastBrace + 1);
        }
        presentationData = JSON.parse(rawJson);
      }
    } catch (e) {
      console.warn('[TurnCard] Failed to parse presentation data:', e);
    }
  }

  const handleOpenSheet = () => {
    if (spreadsheetData) {
      setActivePreview({
        type: 'spreadsheet',
        title: spreadsheetData.title || 'Interactive Costing Workbook',
        data: spreadsheetData
      });
    }
  };

  const handleOpenPresentation = () => {
    if (presentationData) {
      setActivePreview({
        type: 'presentation',
        title: presentationData.title || 'Slides Presentation',
        data: presentationData
      });
    }
  };

  // Proactively pop open the spreadsheet or presentation canvas upon stream completion
  useEffect(() => {
    if (turn.status === 'done' && !hasAutoOpened) {
      if (spreadsheetData) {
        setHasAutoOpened(true);
        handleOpenSheet();
      } else if (presentationData) {
        setHasAutoOpened(true);
        handleOpenPresentation();
      }
    }
  }, [turn.status, spreadsheetData, presentationData, hasAutoOpened]);

  // Helper to inject pills and Smart Image Generation into text
  const renderContent = (text) => {
    if (!text) return null;
    
    // Fix strikethrough and handle citations
    const fixedText = text.replace(/\\~/g, '~').replace(/~/g, '~\u200B');
    
    // Check for ![WEB_IMAGE: "url"] pattern
    const webMatch = fixedText.match(/!\[WEB_IMAGE:\s*"(.*?)"\]/);
    if (webMatch) {
      const url = webMatch[1];
      const before = fixedText.substring(0, webMatch.index);
      const after = fixedText.substring(webMatch.index + webMatch[0].length);
      
      return (
        <>
          {renderContent(before)}
          <WebImage url={url} />
          {renderContent(after)}
        </>
      );
    }

    // Check for ![GENERATE: "prompt"] pattern
    const genMatch = fixedText.match(/!\[GENERATE:\s*"(.*?)"\]/);
    if (genMatch) {
      const prompt = genMatch[1];
      const before = fixedText.substring(0, genMatch.index);
      const after = fixedText.substring(genMatch.index + genMatch[0].length);
      return (
        <>{renderContent(before)}<div style={{ margin: '12px 0' }}><SmartImageGenerator prompt={prompt} accentColor={ATELIER.accent} /></div>{renderContent(after)}</>
      );
    }

    // Check for ![VIDEO: "prompt"] pattern
    const videoMatch = fixedText.match(/!\[VIDEO:\s*"(.*?)"\]/);
    if (videoMatch) {
      const prompt = videoMatch[1];
      const before = fixedText.substring(0, videoMatch.index);
      const after = fixedText.substring(videoMatch.index + videoMatch[0].length);
      return (
        <>{renderContent(before)}<div style={{ margin: '12px 0' }}><SmartVideoGenerator prompt={prompt} accentColor={ATELIER.accent} /></div>{renderContent(after)}</>
      );
    }

    // Check for ![MUSIC: "prompt"] pattern
    const musicMatch = fixedText.match(/!\[MUSIC:\s*"(.*?)"\]/);
    if (musicMatch) {
      const prompt = musicMatch[1];
      const before = fixedText.substring(0, musicMatch.index);
      const after = fixedText.substring(musicMatch.index + musicMatch[0].length);
      return (
        <>{renderContent(before)}<div style={{ margin: '12px 0' }}><SmartMusicGenerator prompt={prompt} accentColor={ATELIER.accent} /></div>{renderContent(after)}</>
      );
    }

    const parts = fixedText.split(/(\[\d+\])/g);
    return parts.map((part, i) => {
      const match = part.match(/\[(\d+)\]/);
      if (match) {
        return <CitationPill key={i} id={match[1]} citations={citations} accentColor={ATELIER.accent} />;
      }
      return part;
    });
  };

  return (
    <>
      <div style={{
        fontFamily: FONTS.sans,
        fontSize: 15,
        lineHeight: 1.8,
        color: ATELIER.inkMid,
        letterSpacing: '-0.01em',
        wordBreak: 'break-word',
      }}>
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          components={{
            a: ({node, children, href, ...props}) => {
               const text = React.Children.toArray(children).map(c => String(c)).join('');
               const match = text.match(/\[(\d+)\]/);
               if (match) {
                 return <CitationPill id={match[1]} citations={citations} accentColor={ATELIER.accent} />;
               }
               return (
                 <a 
                   href={href} 
                   target="_blank" 
                   rel="noopener noreferrer" 
                   style={{ 
                     color: ATELIER.accent, 
                     textDecoration: 'none',
                     fontWeight: 600,
                     borderBottom: `1px solid ${ATELIER.borderAccent}`
                   }} 
                   {...props}
                 >
                   {children}
                 </a>
               );
            },
            p: ({node, children, ...props}) => (
              <p style={{ margin: '0 0 1.25em 0', lineHeight: 1.8 }} {...props}>
                {React.Children.map(children, child => 
                  typeof child === 'string' ? renderContent(child) : child
                )}
              </p>
            ),
            strong: ({node, ...props}) => <strong style={{ color: ATELIER.ink, fontWeight: 700 }} {...props} />,
            ul: ({node, ...props}) => <ul style={{ margin: '0 0 1.25em 0', paddingLeft: '1.5em', listStyleType: 'disc' }} {...props} />,
            ol: ({node, ...props}) => <ol style={{ margin: '0 0 1.25em 0', paddingLeft: '1.5em' }} {...props} />,
            li: ({node, children, ...props}) => (
              <li style={{ marginBottom: '0.5em' }} {...props}>
                {React.Children.map(children, child => 
                  typeof child === 'string' ? renderContent(child) : child
                )}
              </li>
            ),
            h1: ({node, ...props}) => <h1 style={{ fontFamily: FONTS.serif, fontSize: 28, color: ATELIER.ink, margin: '1.5em 0 0.5em 0', fontWeight: 500, letterSpacing: '-0.02em' }} {...props} />,
            h2: ({node, ...props}) => <h2 style={{ fontFamily: FONTS.serif, fontSize: 22, color: ATELIER.ink, margin: '1.25em 0 0.5em 0', fontWeight: 500, borderBottom: `1px solid ${ATELIER.borderSoft}`, paddingBottom: 8 }} {...props} />,
            h3: ({node, ...props}) => <h3 style={{ fontSize: 17, color: ATELIER.ink, margin: '1.25em 0 0.5em 0', fontWeight: 700 }} {...props} />,
            pre: ({ children }) => <>{children}</>,
            code: ({node, inline, className, children, ...props}) => {
              const match = /language-(\w+)/.exec(className || '');
              const codeVal = String(children).replace(/\n$/, '');
              
              const isInline = !className && !codeVal.includes('\n');
              
              if (isInline) {
                return (
                  <code 
                    style={{ 
                      fontFamily: FONTS.mono, 
                      fontSize: '0.9em', 
                      background: ATELIER.bgHover, 
                      padding: '2px 6px', 
                      borderRadius: 6, 
                      color: ATELIER.accent, 
                      fontWeight: 500 
                    }} 
                    {...props}
                  >
                    {children}
                  </code>
                );
              }

              return (
                <PremiumCodeBlock 
                  language={match ? match[1] : ''} 
                  code={codeVal} 
                />
              );
            },
            img: ({node, src, alt, ...props}) => {
              if (src?.startsWith('GENERATE:')) {
                const prompt = src.replace('GENERATE:', '').replace(/"/g, '').trim();
                return <SmartImageGenerator prompt={prompt} accentColor={ATELIER.accent} />;
              }
              if (src?.startsWith('WEB_IMAGE:')) {
                const url = src.replace('WEB_IMAGE:', '').replace(/"/g, '').trim();
                return <WebImage url={url} />;
              }
              return (
                <img 
                  src={src} 
                  alt={alt} 
                  style={{ 
                    maxWidth: '100%', 
                    borderRadius: 12, 
                    margin: '1.5em 0', 
                    boxShadow: ATELIER.shadowM,
                    border: `1px solid ${ATELIER.borderSoft}`
                  }} 
                  {...props} 
                />
              );
            },
            blockquote: ({node, ...props}) => <blockquote style={{ margin: '1.5em 0', padding: '0.5em 1.5em', borderLeft: `4px solid ${ATELIER.accent}`, color: ATELIER.inkSoft, fontStyle: 'italic', background: `${ATELIER.accent}05`, borderRadius: '0 8px 8px 0' }} {...props} />,
            table: ({node, ...props}) => (
              <div style={{ overflowX: 'auto', margin: '1.5em 0', borderRadius: 12, border: `1px solid ${ATELIER.border}`, boxShadow: ATELIER.shadowM }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, textAlign: 'left', wordBreak: 'normal' }} {...props} />
              </div>
            ),
            thead: ({node, ...props}) => <thead style={{ background: ATELIER.bgHover, color: ATELIER.ink }} {...props} />,
            th: ({node, ...props}) => <th style={{ padding: '8px 10px', borderBottom: `2px solid ${ATELIER.border}`, fontWeight: 700 }} {...props} />,
            td: ({node, ...props}) => <td style={{ padding: '8px 10px', borderBottom: `1px solid ${ATELIER.borderSoft}`, color: ATELIER.inkMid }} {...props} />,
            tr: ({node, ...props}) => <tr style={{ transition: 'background 0.2s' }} onMouseOver={(e) => e.currentTarget.style.background = `${ATELIER.accent}03`} onMouseOut={(e) => e.currentTarget.style.background = 'transparent'} {...props} />
          }}
        >
          {(() => {
            let textToRender = displayedText;
            const sIdx = textToRender.indexOf('[SPREADSHEET_DATA]');
            if (sIdx !== -1) textToRender = textToRender.substring(0, sIdx).trim();
            const pIdx = textToRender.indexOf('[PRESENTATION_DATA]');
            if (pIdx !== -1) textToRender = textToRender.substring(0, pIdx).trim();
            return textToRender.replace(/~~/g, '~\u200B~');
          })()}
        </ReactMarkdown>

        {/* Dynamic Spreadsheet Card Widget */}
        {spreadsheetData && (
          <SpreadsheetWidgetCard
            title={spreadsheetData.title || 'Interactive Costing Workbook'}
            sheetCount={spreadsheetData.sheets?.length || 1}
            onOpen={handleOpenSheet}
          />
        )}

        {/* Dynamic Presentation Card Widget */}
        {presentationData && (
          <PresentationWidgetCard
            title={presentationData.title || 'Slides Presentation'}
            slideCount={presentationData.slides?.length || 0}
            onOpen={handleOpenPresentation}
          />
        )}
      </div>
      <ActionRow turn={turn} />
    </>
  );
}

/* ── Image ──────────────────────────────────────────────────── */
function ImageBody({ turn }) {
  const { setExpandedMedia } = useWorkspace();
  const [hovered, setHovered] = useState(false);
  const ratio = aspectRatio(turn.meta?.aspect);

  const handleDownload = async (e) => {
    e.stopPropagation();
    const apiUrl = 'https://api.daggpt.network';
    const proxyUrl = `${apiUrl}/api/download?url=${encodeURIComponent(turn.imageUrl)}`;
    
    const link = document.createElement('a');
    link.href = proxyUrl;
    link.download = `dag-generation-${turn.id}.jpg`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <>
      <RevealEffect id={turn.id} active>
        <div 
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
          onClick={() => setExpandedMedia({ url: turn.imageUrl, type: 'image', title: turn.text })}
          style={{
            maxWidth: 540,
            borderRadius: 14,
            overflow: 'hidden',
            border: `1px solid ${ATELIER.border}`,
            background: ATELIER.bgHover,
            boxShadow: ATELIER.shadowM,
            position: 'relative',
            cursor: 'zoom-in',
          }}
        >
          <div style={{ width: '100%', aspectRatio: ratio }}>
            {turn.imageUrl &&
              <img src={turn.imageUrl} alt="" loading="lazy"
                style={{ display: 'block', width: '100%', height: '100%', objectFit: 'cover' }} />}
          </div>

          {/* Hover Actions */}
          <AnimatePresence>
            {hovered && (
              <motion.div
                initial={{ opacity: 0, y: 4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 4 }}
                transition={{ duration: 0.15 }}
                style={{
                  position: 'absolute',
                  bottom: 12,
                  right: 12,
                  display: 'flex',
                  gap: 8,
                  zIndex: 10,
                }}
              >
                <button
                  onClick={handleDownload}
                  style={{
                    width: 34, height: 34, borderRadius: 10,
                    background: 'rgba(255, 255, 255, 0.95)',
                    backdropFilter: 'blur(8px)',
                    border: `1px solid ${ATELIER.border}`,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    cursor: 'pointer', color: ATELIER.ink,
                  }}
                >
                  <Download size={16} />
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setExpandedMedia({ url: turn.imageUrl, type: 'image', title: turn.text });
                  }}
                  style={{
                    width: 34, height: 34, borderRadius: 10,
                    background: 'rgba(255, 255, 255, 0.95)',
                    backdropFilter: 'blur(8px)',
                    border: `1px solid ${ATELIER.border}`,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    cursor: 'pointer', color: ATELIER.ink,
                  }}
                >
                  <Maximize2 size={16} />
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </RevealEffect>
      <ActionRow turn={turn} />
    </>
  );
}

/* ── Video ──────────────────────────────────────────────────── */
function VideoBody({ turn }) {
  const { setExpandedMedia } = useWorkspace();
  const [hovered, setHovered] = useState(false);
  const ratio = aspectRatio(turn.meta?.aspect || '16:9');

  const handleDownload = async (e) => {
    e.stopPropagation();
    const apiUrl = 'https://api.daggpt.network';
    const proxyUrl = `${apiUrl}/api/download?url=${encodeURIComponent(turn.videoUrl)}`;
    
    const link = document.createElement('a');
    link.href = proxyUrl;
    link.download = `dag-generation-${turn.id}.mp4`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <>
      <RevealEffect id={turn.id} active>
        <div 
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
          onClick={() => setExpandedMedia({ url: turn.videoUrl, type: 'video', title: turn.text })}
          style={{
            maxWidth: 600,
            borderRadius: 14,
            overflow: 'hidden',
            border: `1px solid ${ATELIER.border}`,
            background: '#000',
            boxShadow: ATELIER.shadowM,
            position: 'relative',
            cursor: 'zoom-in',
          }}
        >
          <div style={{ width: '100%', aspectRatio: ratio }}>
            {turn.videoUrl &&
              <video src={turn.videoUrl} controls playsInline preload="metadata"
                style={{ display: 'block', width: '100%', height: '100%', objectFit: 'cover' }} />}
          </div>

          {/* Hover Actions */}
          <AnimatePresence>
            {hovered && (
              <motion.div
                initial={{ opacity: 0, y: 4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 4 }}
                transition={{ duration: 0.15 }}
                style={{
                  position: 'absolute',
                  bottom: 12,
                  right: 12,
                  display: 'flex',
                  gap: 8,
                  zIndex: 10,
                }}
              >
                <button
                  onClick={handleDownload}
                  style={{
                    width: 34, height: 34, borderRadius: 10,
                    background: 'rgba(255, 255, 255, 0.95)',
                    backdropFilter: 'blur(8px)',
                    border: `1px solid ${ATELIER.border}`,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    cursor: 'pointer', color: ATELIER.ink,
                  }}
                >
                  <Download size={16} />
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setExpandedMedia({ url: turn.videoUrl, type: 'video', title: turn.text });
                  }}
                  style={{
                    width: 34, height: 34, borderRadius: 10,
                    background: 'rgba(255, 255, 255, 0.95)',
                    backdropFilter: 'blur(8px)',
                    border: `1px solid ${ATELIER.border}`,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    cursor: 'pointer', color: ATELIER.ink,
                  }}
                >
                  <Maximize2 size={16} />
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </RevealEffect>
      <ActionRow turn={turn} />
    </>
  );
}

/* ── Music ──────────────────────────────────────────────────── */
function MusicBody({ turn }) {
  const tracks = turn.meta?.sunoTracks || [{ audioUrl: turn.audioUrl, imageUrl: turn.imageUrl }];

  return (
    <>
      <RevealEffect id={turn.id} active>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {tracks.map((track, i) => (
            <MusicTrack key={i} track={track} />
          ))}
        </div>
      </RevealEffect>
      <ActionRow turn={turn} />
    </>
  );
}

function MusicTrack({ track }) {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const a = audioRef.current; if (!a) return;
    const onTime = () => setProgress(a.duration ? a.currentTime / a.duration : 0);
    const onEnd  = () => setPlaying(false);
    a.addEventListener('timeupdate', onTime);
    a.addEventListener('ended', onEnd);
    return () => { a.removeEventListener('timeupdate', onTime); a.removeEventListener('ended', onEnd); };
  }, []);

  const toggle = () => {
    const a = audioRef.current; if (!a) return;
    if (playing) { a.pause(); setPlaying(false); } else { a.play(); setPlaying(true); }
  };

  const audioUrl = track.audio_url || track.audioUrl || track.streamAudioUrl;
  const imageUrl = track.image_url || track.imageUrl;
  const title = track.title;

  return (
    <div style={{
      maxWidth: 560,
      padding: 14,
      borderRadius: 14,
      border: `1px solid ${ATELIER.border}`,
      background: 'linear-gradient(135deg, #FCFAFE 0%, #F4EEFE 100%)',
      display: 'flex', alignItems: 'center', gap: 14,
      boxShadow: ATELIER.shadowM,
    }}>
      {imageUrl && (
        <img src={imageUrl} alt="Cover" style={{ width: 44, height: 44, borderRadius: 8, objectFit: 'cover', flexShrink: 0, border: `1px solid ${ATELIER.borderStrong}` }} />
      )}
      <button
        onClick={toggle}
        aria-label={playing ? 'Pause' : 'Play'}
        style={{
          width: 40, height: 40, borderRadius: '50%',
          background: ATELIER.gradient,
          color: '#fff', border: 'none', cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0,
          boxShadow: '0 4px 12px -2px rgba(124,58,237,0.50), inset 0 1px 0 rgba(255,255,255,0.30)',
        }}
      >
</button>
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 4 }}>
        {title && <div style={{ fontSize: 13, fontWeight: 600, color: ATELIER.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{title}</div>}
        <Waveform progress={progress} active={playing} />
      </div>
      <audio ref={audioRef} src={audioUrl} preload="metadata" />
    </div>
  );
}

function Waveform({ progress, active }) {
  const bars = 44;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 2.5, height: 26 }}>
      {Array.from({ length: bars }).map((_, i) => {
        const seed = Math.sin(i * 1.7) * 0.5 + 0.5;
        const h = 5 + seed * 20;
        const played = i / bars <= progress;
        return (
          <motion.div
            key={i}
            animate={{ height: active ? h + Math.sin(Date.now() / 150 + i) * 2 : h }}
            transition={{ duration: 0.1 }}
            style={{
              width: 2.5,
              borderRadius: 2,
              background: played ? ATELIER.accent : ATELIER.borderStrong,
            }}
          />
        );
      })}
    </div>
  );
}

/* ── Boneyard Skeleton Loader ─────────────────────────────────── */
function BoneyardSkeleton({ name = 'text', aspect = '16:9' }) {
  const bones = (boneyardRegistry && boneyardRegistry[name]) || (boneyardRegistry && boneyardRegistry.text) || [];
  
  // Calculate dynamic height based on aspect ratio for media
  const getDynamicHeight = () => {
    if (name === 'image') return 380;
    if (name === 'video') {
      const [w, h] = aspect.split(':').map(Number);
      return Math.round((380 * h) / w);
    }
    if (name === 'music') return 64;
    return 140;
  };

  const dynamicHeight = getDynamicHeight();
  
  return (
    <div style={{ 
      position: 'relative', 
      width: '100%', 
      maxWidth: name === 'text' ? 600 : 380,
      height: dynamicHeight,
      margin: name === 'text' ? '4px 0' : '16px auto',
      overflow: 'hidden',
      borderRadius: 20
    }}>
      {bones.map((bone, i) => {
        // For video main bone, adjust height to dynamic
        const boneHeight = (name === 'video' && bone.height === 214) ? dynamicHeight : bone.height;
        const boneTop = (name === 'video' && bone.y === '38%') ? '40%' : bone.y; // Center play button

        return (
          <motion.div
            key={i}
            initial={{ opacity: 0.3 }}
            animate={{ 
              opacity: [0.3, 0.6, 0.3],
              backgroundPosition: ['-200% 0%', '200% 0%']
            }}
            transition={{ 
              duration: 1.8, 
              repeat: Infinity, 
              delay: i * 0.1,
              ease: "linear"
            }}
            style={{
              position: 'absolute',
              left: bone.x,
              top: boneTop,
              width: bone.width,
              height: boneHeight,
              borderRadius: bone.borderRadius,
              background: 'linear-gradient(110deg, rgba(241, 245, 249, 0.4) 30%, rgba(255, 255, 255, 0.8) 50%, rgba(241, 245, 249, 0.4) 70%)',
              backgroundSize: '200% 100%'
            }}
          />
        );
      })}
    </div>
  );
}

const SMART_PHRASES = [
  "Synthesizing creative intent",
  "Anchoring blockchain context",
  "Optimizing multi-modal stream",
  "Hardening generation parameters",
  "Finalizing Million-Dollar UX"
];

function SmartChatLoader({ turn }) {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setIndex(prev => (prev + 1) % SMART_PHRASES.length);
    }, 2800);
    return () => clearInterval(interval);
  }, []);

  const statusEvents = turn?.statusEvents || [];
  const latestEvent = (statusEvents.length > 0 && typeof statusEvents[statusEvents.length - 1] === 'object') 
    ? statusEvents[statusEvents.length - 1] 
    : null;


  if (latestEvent && latestEvent.statusType === 'exploring') {
    return (
      <ExplorationStatus url={latestEvent.content} />
    );
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '2px 0', marginTop: 4, opacity: 0.8 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
        {[0, 1, 2].map(i => (
          <motion.div
            key={i}
            animate={{ opacity: [0.25, 1, 0.25], y: [0, -2, 0] }}
            transition={{ duration: 1, repeat: Infinity, delay: i * 0.14, ease: 'easeInOut' }}
            style={{ width: 5, height: 5, borderRadius: '50%', background: ATELIER.accent }}
          />
        ))}
      </div>
      <AnimatePresence mode="wait">
        <motion.div
          key={index}
          initial={{ opacity: 0, y: 2 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -2 }}
          transition={{ duration: 0.25 }}
          style={{
            fontFamily: FONTS.mono,
            fontSize: 11,
            color: ATELIER.inkMuted,
            letterSpacing: '0.04em',
            fontWeight: 500
          }}
        >
          {SMART_PHRASES[index] || 'Processing intelligence...'}
        </motion.div>
      </AnimatePresence>
    </div>
  );
}

function ExplorationStatus({ url }) {
  if (!url || typeof url !== 'string') return null;
  // Check if it's a URL or a query
  const isUrl = url.startsWith('http') || url.includes('.');
  const displayLabel = isUrl ? 'Exploring' : 'Searching';
  
  // Clean URL for display if it's a URL
  const displayText = isUrl 
    ? url.replace(/^https?:\/\/(www\.)?/, '').split('/')[0]
    : url;
  
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '4px 0' }}>
      <motion.div 
        animate={{ rotate: 360 }}
        transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={ATELIER.accent} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="11" cy="11" r="8" />
          <path d="m21 21-4.3-4.3" />
        </svg>
      </motion.div>
      <AnimatePresence mode="wait">
        <motion.div
          key={url}
          initial={{ opacity: 0, x: -5 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: 5 }}
          transition={{ duration: 0.4, ease: "easeOut" }}
          style={{
            fontFamily: FONTS.mono,
            fontSize: 12,
            fontWeight: 600,
            color: ATELIER.accent,
            display: 'flex',
            alignItems: 'center',
            gap: 6
          }}
        >
          <span style={{ color: ATELIER.inkMuted, fontWeight: 400 }}>{displayLabel}</span>
          <span style={{ 
            background: ATELIER.accentSoft, 
            padding: '2px 8px', 
            borderRadius: 6,
            border: `1px solid ${ATELIER.borderAccent}`,
            boxShadow: `0 2px 8px ${ATELIER.accentSoft}`,
            maxWidth: 240,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}>
            {displayText}
          </span>
          <motion.span
            animate={{ opacity: [0, 1, 0] }}
            transition={{ duration: 1.5, repeat: Infinity }}
            style={{ fontSize: 14 }}
          >
            ...
          </motion.span>
        </motion.div>
      </AnimatePresence>
    </div>
  );
}



/* ── Error ──────────────────────────────────────────────────── */
function ErrorBody({ error, turn }) {
  const errorMessage = error || turn?.error || turn?.text || 'Generation failed';
  return (
    <div style={{
      padding: '10px 14px',
      borderRadius: 10,
      background: '#FEF2F2',
      border: `1px solid #FECACA`,
      fontFamily: FONTS.sans, fontSize: 13,
      color: ATELIER.error, lineHeight: 1.5,
    }}>{errorMessage}</div>
  );
}

/* ── Markdown to Basic HTML Parser ──────────────────────────── */
function markdownToHtml(md) {
  if (!md) return '';
  // Clean up any spreadsheet tag blocks or suggestions from exported document
  let clean = md.replace(/\[SPREADSHEET_DATA\][\s\S]*?\[\/SPREADSHEET_DATA\]/g, '').trim();
  clean = clean.replace(/\[SUGGESTIONS:[\s\S]*?\]/g, '').trim();
  
  // Split into lines to parse tables
  const lines = clean.split('\n');
  const processedLines = [];
  let inTable = false;
  let tableHeaders = null;
  let tableRows = [];

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();
    
    // Detect markdown table rows
    if (line.startsWith('|') && line.endsWith('|')) {
      const cells = line.split('|').map(c => c.trim()).filter((_, idx, arr) => idx > 0 && idx < arr.length - 1);
      
      if (!inTable) {
        // Look ahead to check if the next line is a separator row
        const nextLine = (lines[i + 1] || '').trim();
        if (nextLine.startsWith('|') && nextLine.includes('-') && nextLine.endsWith('|')) {
          inTable = true;
          tableHeaders = cells;
          i++; // Skip the separator row
          continue;
        }
      } else {
        tableRows.push(cells);
        continue;
      }
    }
    
    // Flush table if we were inside one and current line is not a table row
    if (inTable && (!line.startsWith('|') || !line.endsWith('|'))) {
      processedLines.push(renderHtmlTable(tableHeaders, tableRows));
      inTable = false;
      tableHeaders = null;
      tableRows = [];
    }

    if (!inTable) {
      processedLines.push(lines[i]);
    }
  }

  // Flush table if file ends during a table block
  if (inTable) {
    processedLines.push(renderHtmlTable(tableHeaders, tableRows));
  }

  const htmlResult = processedLines.join('\n');

  return htmlResult
    .replace(/^# (.*?)$/gm, '<h1>$1</h1>')
    .replace(/^## (.*?)$/gm, '<h2>$1</h2>')
    .replace(/^### (.*?)$/gm, '<h3>$1</h3>')
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.*?)\*/g, '<em>$1</em>')
    .replace(/`(.*?)`/g, '<code>$1</code>')
    .replace(/^\- (.*?)$/gm, '<li>$1</li>')
    .replace(/(<li>.*?<\/li>)/gs, '<ul>$1</ul>')
    .replace(/<\/ul>\s*<ul>/g, '')
    .replace(/\n\n/g, '<p></p>')
    .replace(/\n/g, '<br/>');
}

function renderHtmlTable(headers, rows) {
  const headerHtml = headers.map(h => `<th style="padding: 10px 14px; border-bottom: 2px solid #cbd5e1; background: #f8fafc; font-weight: 600; text-align: left; color: #1e293b; font-size: 13px;">${h}</th>`).join('');
  
  const rowsHtml = rows.map((r, rIdx) => {
    const bg = rIdx % 2 === 0 ? '#ffffff' : '#f8fafc';
    const cellsHtml = r.map(c => `<td style="padding: 10px 14px; border-bottom: 1px solid #e2e8f0; font-size: 13px; color: #334155; line-height: 1.5;">${c}</td>`).join('');
    return `<tr style="background: ${bg};">${cellsHtml}</tr>`;
  }).join('');

  return `
    <table style="width: 100%; border-collapse: collapse; margin: 24px 0; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif; box-shadow: 0 1px 3px rgba(0,0,0,0.05); border-radius: 8px; overflow: hidden; border: 1px solid #e2e8f0;">
      <thead>
        <tr>${headerHtml}</tr>
      </thead>
      <tbody>
        ${rowsHtml}
      </tbody>
    </table>
  `;
}

/* ── Export Dropdown Component ──────────────────────────────── */
function ExportDropdown({ turn }) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const getCleanText = () => {
    let clean = turn.text || '';
    clean = clean.replace(/\[SPREADSHEET_DATA\][\s\S]*?\[\/SPREADSHEET_DATA\]/g, '');
    clean = clean.replace(/\[SUGGESTIONS:[\s\S]*?\]/g, '');
    return clean.trim();
  };

  const handleExportText = () => {
    const text = getCleanText();
    const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `DAGGPT_Export_${Date.now()}.txt`;
    link.click();
    URL.revokeObjectURL(url);
    setIsOpen(false);
  };

  const handleExportWord = () => {
    const rawText = getCleanText();
    const htmlContent = markdownToHtml(rawText);
    const docContent = `
      <html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:w="urn:schemas-microsoft-com:office:word" xmlns="http://www.w3.org/TR/REC-html40">
      <head>
        <meta charset="utf-8">
        <title>DAGGPT Premium Export</title>
        <style>
          body { font-family: 'Segoe UI', Arial, sans-serif; padding: 20px; line-height: 1.6; color: #1e293b; }
          h1 { color: #4f46e5; font-size: 24px; border-bottom: 1px solid #e2e8f0; padding-bottom: 8px; margin-top: 0; }
          h2 { color: #0f172a; font-size: 18px; margin-top: 24px; border-bottom: 1px solid #f1f5f9; padding-bottom: 4px; }
          h3 { color: #1e1b4b; font-size: 14px; margin-top: 16px; }
          p { margin-bottom: 12px; }
          strong { color: #0f172a; }
          ul { padding-left: 20px; margin-bottom: 12px; }
          li { margin-bottom: 4px; }
          code { font-family: 'Courier New', Courier, monospace; background: #f1f5f9; padding: 2px 4px; border-radius: 4px; color: #db2777; }
        </style>
      </head>
      <body>
        <div style="font-size: 11px; color: #94a3b8; text-transform: uppercase; letter-spacing: 0.1em; margin-bottom: 20px;">
          DAGGPT Premium Intelligence Hub &bull; Document Export
        </div>
        ${htmlContent}
        <hr style="border: none; border-top: 1px dashed #e2e8f0; margin-top: 40px; margin-bottom: 10px;">
        <div style="font-size: 10px; color: #94a3b8; text-align: center;">
          Generated dynamically by DAGGPT Platform on ${new Date().toLocaleDateString()}
        </div>
      </body>
      </html>
    `;
    const blob = new Blob([docContent], { type: 'application/msword;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `DAGGPT_Document_${Date.now()}.doc`;
    link.click();
    URL.revokeObjectURL(url);
    setIsOpen(false);
  };

  const handleExportPDF = () => {
    const rawText = getCleanText();
    const htmlContent = markdownToHtml(rawText);
    const printWindow = window.open("", "_blank");
    if (!printWindow) return;

    printWindow.document.write(`
      <html>
      <head>
        <title>DAGGPT Premium Export</title>
        <link href="https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700&family=Playfair+Display:ital,wght@0,400;0,500;1,400&display=swap" rel="stylesheet">
        <style>
          body { 
            font-family: 'Outfit', sans-serif; 
            padding: 40px; 
            line-height: 1.8; 
            color: #1e293b; 
            max-width: 800px;
            margin: 0 auto;
          }
          h1 { 
            font-family: 'Playfair Display', serif;
            color: #4f46e5; 
            font-size: 32px; 
            border-bottom: 2px solid #e2e8f0; 
            padding-bottom: 12px; 
            margin-top: 0; 
            margin-bottom: 24px;
            font-weight: 400;
          }
          h2 { 
            font-family: 'Playfair Display', serif;
            color: #0f172a; 
            font-size: 24px; 
            margin-top: 32px; 
            border-bottom: 1px solid #f1f5f9; 
            padding-bottom: 6px;
            font-weight: 400;
          }
          h3 { 
            color: #1e1b4b; 
            font-size: 18px; 
            margin-top: 24px; 
            font-weight: 600;
          }
          p { margin-bottom: 16px; font-weight: 300; }
          strong { color: #0f172a; font-weight: 600; }
          ul { padding-left: 24px; margin-bottom: 16px; }
          li { margin-bottom: 6px; font-weight: 300; }
          code { 
            font-family: monospace; 
            background: #f1f5f9; 
            padding: 2px 6px; 
            border-radius: 6px; 
            color: #db2777; 
            font-size: 0.9em;
          }
          .header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            border-bottom: 1px solid #f1f5f9;
            padding-bottom: 12px;
            margin-bottom: 30px;
          }
          .header-title {
            font-size: 11px; 
            color: #94a3b8; 
            text-transform: uppercase; 
            letter-spacing: 0.15em;
            font-weight: 600;
          }
          .footer {
            margin-top: 60px;
            border-top: 1px dashed #e2e8f0;
            padding-top: 16px;
            font-size: 10px;
            color: #94a3b8;
            text-align: center;
          }
          @media print {
            body { padding: 20px; }
            .no-print { display: none; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="header-title">DAGGPT Premium Export</div>
          <div style="font-size: 11px; color: #94a3b8;">${new Date().toLocaleDateString()}</div>
        </div>
        ${htmlContent}
        <div class="footer">
          Generated via DAGGPT Web Workspace &bull; Blockchain-anchored Creative Engine
        </div>
        <script>
          window.onload = function() {
            window.print();
          };
        </script>
      </body>
      </html>
    `);
    printWindow.document.close();
    setIsOpen(false);
  };

  return (
    <div ref={dropdownRef} style={{ position: 'relative', display: 'inline-block' }}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          padding: '4px 10px',
          borderRadius: 8,
          fontSize: 11.5,
          fontWeight: 600,
          background: 'rgba(255, 255, 255, 0.7)',
          border: '1px solid rgba(226, 232, 240, 0.8)',
          boxShadow: '0 2px 6px rgba(0,0,0,0.02)',
          color: '#475569',
          cursor: 'pointer',
          transition: 'all 0.15s ease-in-out',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = '#fff';
          e.currentTarget.style.borderColor = '#cbd5e1';
          e.currentTarget.style.boxShadow = '0 3px 8px rgba(0,0,0,0.04)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = 'rgba(255, 255, 255, 0.7)';
          e.currentTarget.style.borderColor = 'rgba(226, 232, 240, 0.8)';
          e.currentTarget.style.boxShadow = '0 2px 6px rgba(0,0,0,0.02)';
        }}
      >
        <Download size={12} strokeWidth={1.9} />
        Export As
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -4, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -4, scale: 0.95 }}
            transition={{ duration: 0.12 }}
            style={{
              position: 'absolute',
              bottom: '100%',
              left: 0,
              marginBottom: 6,
              width: 180,
              background: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(16px)',
              border: '1px solid rgba(226, 232, 240, 0.8)',
              borderRadius: 12,
              padding: '6px',
              boxShadow: '0 10px 25px -5px rgba(0,0,0,0.06), 0 8px 10px -6px rgba(0,0,0,0.04)',
              zIndex: 100,
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
            }}
          >
            <button
              onClick={handleExportPDF}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '8px 10px',
                borderRadius: 8,
                fontSize: 12,
                fontWeight: 500,
                color: '#1e293b',
                background: 'transparent',
                border: 'none',
                textAlign: 'left',
                width: '100%',
                cursor: 'pointer',
                transition: 'background 0.15s',
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = '#f1f5f9'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <polyline points="14 2 14 8 20 8" />
              </svg>
              Export PDF Document
            </button>

            <button
              onClick={handleExportWord}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '8px 10px',
                borderRadius: 8,
                fontSize: 12,
                fontWeight: 500,
                color: '#1e293b',
                background: 'transparent',
                border: 'none',
                textAlign: 'left',
                width: '100%',
                cursor: 'pointer',
                transition: 'background 0.15s',
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = '#f1f5f9'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <polyline points="14 2 14 8 20 8" />
              </svg>
              Export Word Document
            </button>

            <button
              onClick={handleExportText}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '8px 10px',
                borderRadius: 8,
                fontSize: 12,
                fontWeight: 500,
                color: '#1e293b',
                background: 'transparent',
                border: 'none',
                textAlign: 'left',
                width: '100%',
                cursor: 'pointer',
                transition: 'background 0.15s',
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = '#f1f5f9'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <polyline points="14 2 14 8 20 8" />
              </svg>
              Export Plain Text
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/* ── Action row ─────────────────────────────────────────────── */
function ActionRow({ turn }) {
  const [copied, setCopied] = useState(false);

  if (turn?.meta?.isResetTurn) {
    return null;
  }

  const handleCopy = async () => {
    if (!turn?.text) return;
    try {
      await navigator.clipboard.writeText(turn.text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  return (
    <div style={{
      marginTop: 10,
      display: 'flex', alignItems: 'center', gap: 4,
    }}>
      <ActionBtn 
        icon={copied ? <Check size={13} strokeWidth={1.9} /> : <Copy size={13} strokeWidth={1.9} />} 
        label={copied ? "Copied" : "Copy response"} 
        onClick={handleCopy} 
      />
      <ActionBtn icon="M14 9V5a3 3 0 00-3-3l-4 9v11h11.28a2 2 0 002-1.7l1.38-9a2 2 0 00-2-2.3zM7 22H4a2 2 0 01-2-2v-7a2 2 0 012-2h3" label="Like" />
      <ActionBtn icon="M1 4v6h6M23 20v-6h-6M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15" label="Regenerate" />
      {turn.text && <ExportDropdown turn={turn} />}
      <span style={{ flex: 1 }} />
      {turn.status === 'done' && (
        <span style={{
          display: 'inline-flex', alignItems: 'center', gap: 5,
          fontFamily: FONTS.mono, fontSize: 9.5, letterSpacing: '0.18em',
          color: ATELIER.accent, fontWeight: 700, textTransform: 'uppercase',
          padding: '4px 8px',
          background: ATELIER.accentSoft,
          border: `1px solid ${ATELIER.borderAccent}`,
          borderRadius: 999,
        }}>
          <span style={{
            width: 5, height: 5, borderRadius: '50%',
            background: ATELIER.accent,
            boxShadow: `0 0 6px ${ATELIER.accent}`,
          }} />
          Anchored
        </span>
      )}
    </div>
  );
}

function ActionBtn({ icon, label, onClick }) {
  const [hover, setHover] = useState(false);
  return (
    <button
      type="button"
      title={label}
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        width: 28, height: 28, borderRadius: 7,
        background: hover ? ATELIER.bgHover : 'transparent',
        border: 'none',
        cursor: 'pointer',
        color: hover ? ATELIER.ink : ATELIER.inkMuted,
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        transition: 'background 0.12s, color 0.12s',
      }}
    >
      {typeof icon === 'string' ? (
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
          stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
          <path d={icon} />
        </svg>
      ) : (
        icon
      )}
    </button>
  );
}

function SparkleIcon({ size = 10 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
      <path d={ICON.sparkle} />
    </svg>
  );
}

/* ── helpers ────────────────────────────────────────────────── */
function aspectRatio(a) {
  if (!a) return '1 / 1';
  if (a.includes(':')) return a.replace(':', ' / ');
  return '1 / 1';
}

function formatTime(ts) {
  if (!ts) return '';
  const d = new Date(ts);
  const h = d.getHours();
  const m = d.getMinutes().toString().padStart(2, '0');
  const ampm = h >= 12 ? 'PM' : 'AM';
  const h12 = ((h % 12) || 12);
  return `${h12}:${m} ${ampm}`;
}

/* ── Premium Code Block Component ────────────────────────────── */
export function PremiumCodeBlock({ language, code }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy code block:', err);
    }
  };

  const displayLang = language || 'code';

  return (
    <div style={{
      margin: '24px 0',
      borderRadius: 12,
      border: '1px solid rgba(226, 232, 240, 0.9)',
      boxShadow: '0 4px 16px rgba(0, 0, 0, 0.02), 0 2px 4px rgba(0, 0, 0, 0.01)',
      background: '#ffffff',
      overflow: 'hidden',
      fontFamily: FONTS.sans
    }}>
      {/* Code Header Bar */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '10px 16px',
        background: '#f8fafc',
        borderBottom: '1px solid rgba(226, 232, 240, 0.9)',
        userSelect: 'none'
      }}>
        <span style={{
          fontSize: 11,
          fontWeight: 700,
          color: '#64748b',
          textTransform: 'uppercase',
          letterSpacing: '0.08em',
          display: 'flex',
          alignItems: 'center',
          gap: 6
        }}>
          <span style={{ width: 6, height: 6, borderRadius: '50%', background: ATELIER.accent }} />
          {displayLang}
        </span>
        <button
          type="button"
          onClick={handleCopy}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 5,
            padding: '5px 10px',
            background: 'transparent',
            border: 'none',
            borderRadius: 6,
            fontSize: 11,
            fontWeight: 600,
            color: '#475569',
            cursor: 'pointer',
            transition: 'all 0.15s ease-in-out',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.color = ATELIER.accent;
            e.currentTarget.style.background = 'rgba(79, 70, 229, 0.04)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.color = '#475569';
            e.currentTarget.style.background = 'transparent';
          }}
        >
          {copied ? (
            <>
              <Check size={12} strokeWidth={2} style={{ color: ATELIER.accent }} />
              <span style={{ color: ATELIER.accent }}>Copied!</span>
            </>
          ) : (
            <>
              <Copy size={12} strokeWidth={2} />
              <span>Copy code</span>
            </>
          )}
        </button>
      </div>

      {/* Code Area */}
      <div style={{ padding: '14px 20px', background: '#fafbfc', overflowX: 'auto' }}>
        <SyntaxHighlighter
          language={language}
          style={oneLight}
          customStyle={{
            background: 'transparent',
            padding: 0,
            margin: 0,
            fontSize: '13px',
            lineHeight: '1.6',
            fontFamily: FONTS.mono,
            border: 'none'
          }}
          codeTagProps={{
            style: {
              fontFamily: FONTS.mono,
              background: 'transparent',
              fontSize: '13px',
              color: '#0f172a'
            }
          }}
        >
          {code}
        </SyntaxHighlighter>
      </div>
    </div>
  );
}
